import type { TerrainMask } from '@hooks'
import { coordHash } from '@utils'
import type { GeoInstance } from './instanced-mesh'

export interface DecorationConfig {
  /** Candidate grid cell size — smaller = denser candidates */
  cellSize: number
  /** Min world-space distance to any track */
  minTrackDist: number
  /** Min world-space distance between placed decorations of this family */
  minSpacing: number
  /** Per-candidate terrain acceptance test */
  allow: (wx: number, wz: number, mask: TerrainMask) => boolean
  /** Per-candidate score [0,1] — higher scores placed first in greedy pass */
  score: (wx: number, wz: number) => number
  /** Fraction [0,1] of accepted candidates to actually keep */
  density: number
}

interface Candidate {
  wx: number
  wz: number
  score: number
}

/**
 * Shared deterministic decoration placement pipeline.
 * Returns world-space positions after greedy min-spacing acceptance.
 * Call-order independent: uses coordinate-based hashing, not global seededRandom.
 */
export function spawnDecorations(
  mask: TerrainMask,
  config: DecorationConfig,
  salt: number,
): { wx: number; wz: number }[] {
  const { cellSize, minTrackDist, minSpacing, allow, score, density } = config
  const { sampleTrackDist, side, centerOffset } = mask

  const grid = Math.round(side / cellSize)
  const halfJitter = cellSize * 0.45

  const candidates: Candidate[] = []

  for (let row = 0; row < grid; row++) {
    for (let col = 0; col < grid; col++) {
      const cx = (col / grid - 0.5) * side + centerOffset
      const cz = (row / grid - 0.5) * side

      if (!allow(cx, cz, mask)) continue
      if (sampleTrackDist(cx, cz) < minTrackDist) continue

      const s = score(cx, cz)
      if (s > density) continue

      const jx = (coordHash(cx, cz, salt + 1) - 0.5) * 2 * halfJitter
      const jz = (coordHash(cx, cz, salt + 2) - 0.5) * 2 * halfJitter

      candidates.push({ wx: cx + jx, wz: cz + jz, score: s })
    }
  }

  // Sort ascending so lowest (rarest) scores place first — most "chosen" feel
  candidates.sort((a, b) => a.score - b.score)

  // Greedy spacing pass with spatial hash — O(n) instead of O(n²)
  const placed: { wx: number; wz: number }[] = []
  const minSpacing2 = minSpacing * minSpacing
  const spacingGrid = new Map<string, { wx: number; wz: number }>()
  const spacingKey = (wx: number, wz: number) =>
    `${Math.floor(wx / minSpacing)},${Math.floor(wz / minSpacing)}`

  for (const c of candidates) {
    const gx = Math.floor(c.wx / minSpacing)
    const gz = Math.floor(c.wz / minSpacing)
    let tooClose = false
    outer: for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const p = spacingGrid.get(`${gx + dx},${gz + dz}`)
        if (!p) continue
        const ddx = c.wx - p.wx
        const ddz = c.wz - p.wz
        if (ddx * ddx + ddz * ddz < minSpacing2) {
          tooClose = true
          break outer
        }
      }
    }
    if (!tooClose) {
      placed.push({ wx: c.wx, wz: c.wz })
      spacingGrid.set(spacingKey(c.wx, c.wz), { wx: c.wx, wz: c.wz })
    }
  }

  return placed
}

export function groupByVariant(
  positions: { wx: number; wz: number }[],
  variantCount: number,
  salt: number,
  getInstance: (wx: number, wz: number) => Omit<GeoInstance, 'wx' | 'wz'>,
): GeoInstance[][] {
  const perVariant: GeoInstance[][] = Array.from({ length: variantCount }, () => [])
  for (const { wx, wz } of positions) {
    const vi = Math.floor(coordHash(wx, wz, salt + 10) * variantCount)
    perVariant[vi].push({ wx, wz, ...getInstance(wx, wz) })
  }
  return perVariant
}
